/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * ProfileSetupScreen — onboarding step after OTP + EULA, before emergency
 * contacts. Collects a display name (required) and a profile photo (optional).
 * NO username — phone is the unique identity.
 *
 * On Continue it uploads the photo (if chosen) to Firebase Storage, then writes
 * displayName + photoURL to /users/{uid}/profile via a self-scoped multi-path
 * update (the SAME path ensureUserProfile seeds — no new RTDB paths), mirrors
 * the values into the Firebase Auth user record, and calls onComplete.
 *
 * Reuses the proven photo-pick / upload pattern from EditProfileSheet
 * (Camera.getPhoto base64 → Blob → Storage → getDownloadURL).
 */

import { useCallback, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { updateProfile } from "firebase/auth";
import { ref, update } from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { Camera as CameraIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as Sentry from "@sentry/react";
import { auth, db } from "@/lib/firebase";
import { base64ToBytes } from "@/utils/base64";
import OnboardingShell from "@/components/onboarding/OnboardingShell";

const AVATAR_UPLOAD_TIMEOUT_MS = 25_000;
const MAX_NAME_LENGTH = 40;

interface Props {
  /** Called once displayName (+ optional photoURL) are written successfully. */
  onComplete: () => void;
  /** Back chevron — return to the prior onboarding step. Optional. */
  onBack?: () => void;
  step: number;
  totalSteps: number;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

export default function ProfileSetupScreen({
  onComplete,
  onBack,
  step,
  totalSteps,
}: Props) {
  const [name, setName] = useState("");
  const [pendingPhotoBlob, setPendingPhotoBlob] = useState<Blob | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0;

  const pickPhoto = useCallback(async (source: "camera" | "library") => {
    if (!Capacitor.isNativePlatform()) {
      toast.error("Photo picking is available in the app.");
      return;
    }
    setPicking(true);
    try {
      const result = await Camera.getPhoto({
        quality: 80,
        allowEditing: true,
        resultType: CameraResultType.Base64,
        source: source === "camera" ? CameraSource.Camera : CameraSource.Photos,
        width: 1024,
        height: 1024,
      });
      const base64 = result.base64String;
      if (!base64) throw new Error("camera-no-base64");
      const mime = result.format === "png" ? "image/png" : "image/jpeg";
      const blob = new Blob([base64ToBytes(base64)], { type: mime });
      setPendingPhotoBlob(blob);
      // Revoke any prior object URL before replacing it.
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPhotoPreview(url);
    } catch (err) {
      const e = err as { message?: string };
      const msg = e.message?.toLowerCase() ?? "";
      if (msg.includes("cancel")) {
        // user dismissed the picker — silent
      } else if (msg.includes("denied")) {
        toast.error("Permission denied. Enable photo access in Settings.");
      } else {
        console.error("[ProfileSetupScreen] camera.getPhoto failed:", err);
        toast.error("Couldn't add that photo. Try again.");
      }
    } finally {
      setPicking(false);
    }
  }, []);

  const handleContinue = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error("You're signed out. Please sign in again.");
      return;
    }
    if (!nameValid) return;
    setSaving(true);
    try {
      // 1. Upload the photo (optional) to Storage.
      let photoURL: string | null = null;
      if (pendingPhotoBlob) {
        const sRef = storageRef(getStorage(), `avatars/${user.uid}/profile.jpg`);
        await Promise.race([
          uploadBytes(sRef, pendingPhotoBlob, {
            contentType: pendingPhotoBlob.type || "image/jpeg",
            cacheControl: "public, max-age=300",
          }),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error("avatar-upload-timeout")),
              AVATAR_UPLOAD_TIMEOUT_MS,
            ),
          ),
        ]);
        photoURL = await getDownloadURL(sRef);
      }

      // 2. Write displayName (+ photoURL if chosen) to the existing
      //    /users/{uid}/profile path. Self-scoped multi-path update — no new
      //    RTDB paths, no rules change.
      const updates: Record<string, string> = {
        [`users/${user.uid}/profile/displayName`]: trimmedName,
      };
      if (photoURL) updates[`users/${user.uid}/profile/photoURL`] = photoURL;
      await update(ref(db), updates);

      // 3. Mirror into the Firebase Auth user record so consumers reading
      //    user.displayName / user.photoURL get the values without a reload.
      const authPatch: { displayName?: string; photoURL?: string } = {
        displayName: trimmedName,
      };
      if (photoURL) authPatch.photoURL = photoURL;
      await updateProfile(user, authPatch);

      onComplete();
    } catch (err) {
      console.error("[ProfileSetupScreen] save failed:", err);
      Sentry.captureException(err, { tags: { area: "onboarding.profile" } });
      toast.error("Couldn't save your profile. Check your connection and retry.");
    } finally {
      setSaving(false);
    }
  }, [nameValid, pendingPhotoBlob, trimmedName, onComplete]);

  const initials = initialsOf(name);

  return (
    <OnboardingShell
      step={step}
      totalSteps={totalSteps}
      onBack={onBack}
      ariaLabel="Set up your profile"
      ctaLabel="Continue"
      ctaDisabled={!nameValid}
      ctaBusy={saving}
      onCta={handleContinue}
    >
      <div className="mx-auto flex max-w-md flex-col items-center">
        {/* ── Avatar hero ──────────────────────────────────────────────────
            Big, top-centered circular avatar as the focal point. A soft
            gradient halo sits behind it so an empty avatar still reads as the
            hero, not a placeholder. Tapping anywhere on it opens the library
            picker. */}
        <div className="relative mt-4 flex flex-col items-center">
          <div
            className="absolute -inset-6 rounded-full bg-gradient-to-b from-[#7C52F5]/15 to-transparent blur-2xl"
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={() => pickPhoto("library")}
            disabled={picking || saving}
            aria-label={photoPreview ? "Change profile photo" : "Add profile photo"}
            className="relative flex h-36 w-36 items-center justify-center rounded-full bg-white shadow-[0_22px_48px_-20px_rgba(124,82,245,0.85)] ring-1 ring-[#E0D8F3] active:scale-[0.97] transition-transform disabled:opacity-60"
          >
            {photoPreview ? (
              <img
                src={photoPreview}
                alt="Profile preview"
                className="h-full w-full rounded-full object-cover"
              />
            ) : initials ? (
              <span className="text-4xl font-bold text-[#7C52F5]">{initials}</span>
            ) : picking ? (
              <Loader2
                className="h-8 w-8 animate-spin text-[#7C52F5]"
                aria-hidden="true"
              />
            ) : (
              <CameraIcon className="h-10 w-10 text-[#A89FC4]" aria-hidden="true" />
            )}
            {/* Camera badge — affordance that the avatar is editable. */}
            <span
              className="absolute bottom-1 right-1 flex h-10 w-10 items-center justify-center rounded-full bg-[#7C52F5] text-white shadow-[0_8px_18px_-6px_rgba(124,82,245,0.9)] ring-4 ring-white"
              aria-hidden="true"
            >
              <CameraIcon className="h-4 w-4" />
            </span>
          </button>
          {/* Tap-to-add hint / take-a-photo option directly under the hero. */}
          {Capacitor.isNativePlatform() ? (
            <button
              type="button"
              onClick={() => pickPhoto("camera")}
              disabled={picking || saving}
              className="mt-4 text-sm font-semibold text-[#7C52F5] active:opacity-70 disabled:opacity-60"
            >
              {photoPreview ? "Retake photo" : "Take a photo instead"}
            </button>
          ) : (
            <p className="mt-4 text-sm font-medium text-[#A89FC4]">
              {photoPreview ? "Looking good" : "Tap to add a photo"}
            </p>
          )}
        </div>

        {/* ── Title ────────────────────────────────────────────────────── */}
        <h1 className="mt-8 text-center text-2xl font-bold tracking-tight text-[#2A2342]">
          Let's set up your profile
        </h1>
        <p className="mt-2 text-center text-[15px] leading-relaxed text-[#6B5FA0]">
          Add a name and photo so your circle recognizes you.
        </p>

        {/* ── Name input ───────────────────────────────────────────────── */}
        <div className="mt-7 w-full text-left">
          <label
            htmlFor="profile-name"
            className="mb-2 block px-1 text-xs font-medium text-[#6B5FA0]"
          >
            Your name
          </label>
          <input
            id="profile-name"
            type="text"
            autoComplete="name"
            value={name}
            maxLength={MAX_NAME_LENGTH}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && nameValid && !saving) handleContinue();
            }}
            placeholder="e.g. Jordan Carter"
            className="w-full rounded-2xl border border-[#E0D8F3] bg-white px-4 py-3.5 text-base text-[#2A2342] placeholder:text-[#A89FC4] shadow-[0_8px_24px_-16px_rgba(124,82,245,0.5)] focus:border-[#7C52F5] focus:outline-none focus:ring-2 focus:ring-[#7C52F5]/20"
          />
          {/* Live greeting — fills the same vertical slot whether or not a name
              is present, so the layout doesn't jump as the user types. */}
          <p
            className="mt-2.5 px-1 text-sm font-medium text-[#7C52F5] transition-opacity duration-200"
            style={{ opacity: trimmedName.length > 0 ? 1 : 0 }}
            aria-live="polite"
          >
            {trimmedName.length > 0 ? `Hey ${trimmedName} 👋` : " "}
          </p>
        </div>
      </div>
    </OnboardingShell>
  );
}
